import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import { useAccountReport } from '../../DataStore/AccountReportContext';
import { useNavigate } from 'react-router-dom';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import functions from '../../util/functions';
import Pagination from '../../components/Common/Pagination';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import printContent from '../../util/araknerdlib';
import AlertDateSelector from '../../components/Report/AlertDateSelector';
import { useCurrency } from '../../util/SystemVariables';
import MoveLedgerModal from '../../components/Report/MoveLedgerModal';

const AccountStatementPage = () => {
   const { accountInfo, getStatement, statement, metaData, setCurrentPage, dispatch } = useAccountReport();
     const navigate = useNavigate();
     const statementRef = useRef();
     const [showAlert, setShowAlert] = useState(false);
     const currency = useCurrency();
   
     // ✅ reconcile mode + multi select
     const [reconcileMode, setReconcileMode] = useState(false);
     const [selectedIds, setSelectedIds] = useState([]); // store transaction ids
     const [showMover, setShowMover] = useState(false);
   
     useEffect(() => {
       if (!accountInfo) {
         navigate('/');
       } else {
         getStatement(true);
       }
     }, []);
   
     // whenever we exit reconcile mode, clear selections
     useEffect(() => {
       if (!reconcileMode) setSelectedIds([]);
     }, [reconcileMode]);
   
      const transactionIds = useMemo(() => {
       return Array.isArray(statement?.transactions)
         ? statement.transactions.map((t) => t.id) // assumes each transaction has id
         : [];
     }, [statement]);
   
     if (!accountInfo) return <></>;
   
     const handleConfirmDateRange = async (range) => {
       dispatch({ type: 'dateRange', payload: range });
       await getStatement(true, range);
     };
   
     // -------- selection logic (same style you like) --------
     const toggleSelection = (id) => {
       setSelectedIds((prev) =>
         prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
       );
     };
   
     const isSelected = (id) => selectedIds.includes(id);
   
    
     const allSelected =
       reconcileMode &&
       transactionIds.length > 0 &&
       selectedIds.length === transactionIds.length;
   
     const handleToggleAll = (checked) => {
       if (!reconcileMode) return;
       setSelectedIds(checked ? transactionIds : []);
     };
   
     const toggleReconcileMode = () => {
       setReconcileMode((p) => !p);
     };
   
     const PageActions = () => (
       <>
         <ActionDropdownItem iconClass="fa fa-calendar-alt" label="Filter Date Range" onClick={() => setShowAlert(true)} />
         <ActionDropdownItem iconClass="fa fa-sync" label="Refresh" onClick={() => getStatement(true)} />
   
         {/* ✅ Toggle reconcile mode */}
      {["Asset", "Liability", "Equity"].includes(accountInfo.account_type) && (
        <ActionDropdownItem
           iconClass={reconcileMode ? "fa fa-times-circle text-danger" : "fa fa-check-circle text-primary"}
           label={reconcileMode ? "Exit Reconcile" : "Reconcile"}
           onClick={toggleReconcileMode}
         />
      )}
       <ActionDropdownItem iconClass="fa fa-exchange" label="Perform Contra" onClick={() => setShowMover(true)} disabled={selectedIds.length === 0 || !reconcileMode}/>
       <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={() => printContent(statementRef, "Account Statement")} />
       </>
     );
    return (
        <>
        <PageContainer>
            <MainContent>
                {/* Page Title */}
                <PageTitle
                    title={accountInfo?.account_name || 'Not found'}
                    description={`${accountInfo?.account_number || ''} | Account Statement`}
                    iconClass="fa fa-university"
                    sticky={true}
                    PageActions={PageActions}
                    Pagination={metaData ? <Pagination
                        metaData={metaData}
                        onPageChange={(page) => setCurrentPage(page)}
                        loadMore={true}
                /> : <></>}
                />

                {/* Statement Content */}
                <div ref={statementRef}>
                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 2.5 }}>
                            <h4>Statement of Account</h4>
                            <p>
                                <strong>Account Name:</strong> {accountInfo?.account_name || 'Not found'} <br />
                                <strong>Account Number:</strong> {accountInfo?.account_number || 'N/A'} <br />
                                <strong> Currency:</strong> {currency || 'N/A'}
                            </p>
                            {functions.isObject(statement) && statement.summary && ( <p>
                                <strong>Statement Period:</strong>{' '}
                                {statement.summary.opening_date && <>{functions.formatDate(statement.summary.opening_date)} - {functions.formatDate(statement.summary.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(statement) && statement.summary && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Opening Balance</th>
                                            <td>{functions.numberFormat(statement.summary?.opening_balance || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Debits</th>
                                            <td>{functions.numberFormat(statement.summary?.total_debits || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Credits</th>
                                            <td>{functions.numberFormat(statement.summary?.total_credits || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Closing Balance</th>
                                            <td>{functions.numberFormat(statement.summary?.closing_balance || 0)}</td>

                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                   <div className="card mb-4">
              <div className="card-body">
               {reconcileMode && (
                    <Notification
                        type="warning"
                        message={
                        <>
                            <strong>Reconcile mode enabled.</strong>{" "}
                            Select one or more transactions to reconcile.
                            <span className="ml-2 text-muted">
                            ({selectedIds.length} selected)
                            </span>
                        </>
                        }
                        closable={false}
                    />
                    )}

                {functions.isObject(statement) && (
                  <table className="table table-striped table-bordered">
                    <thead className="thead-light">
                      <tr>
                        {/* ✅ checkbox column only in reconcile mode */}
                        {reconcileMode && (
                          <th style={{ width: 40 }} className="text-center">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={(e) => handleToggleAll(e.target.checked)}
                            />
                          </th>
                        )}
                        <th>Record Date</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Trans. Date</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Array.isArray(statement.transactions) ? (
                        statement.transactions.map((transaction, index) => {
                          // IMPORTANT: you need a stable id.
                          // If your API doesn't return transaction.id, use `${transaction.transaction_date}-${transaction.transaction_ref}-${index}`
                          const rowId = transaction.id ?? index;

                          return (
                            <tr
                              key={rowId}
                              className={reconcileMode && isSelected(rowId) ? "selected-row" : ""}
                              onClick={() => {
                                if (reconcileMode) toggleSelection(rowId);
                              }}
                              style={reconcileMode ? { cursor: "pointer" } : undefined}
                            >
                              {reconcileMode && (
                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected(rowId)}
                                    onChange={() => toggleSelection(rowId)}
                                  />
                                </td>
                              )}
                              <td style={{ whiteSpace: 'nowrap' }}>{transaction.created_at}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{functions.formatDate(transaction.transaction_date)}</td>
                              <td>{transaction.transaction_ref || '-'}</td>
                              <td>{transaction.description || '-'}</td>
                              <td>{transaction.debit > 0 ? functions.numberFormat(transaction.debit) : ''}</td>
                              <td>{transaction.credit > 0 ? functions.numberFormat(transaction.credit) : ''}</td>
                              <td>{functions.numberFormat(transaction.running_balance)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={reconcileMode ? 7 : 6} className="text-center">
                            No transactions available for this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}

                {statement === '404' && (
                  <Notification type="info" message="Statement results not found" closable={false} />
                )}
                {!statement && <ActivityLoader />}
              </div>
            </div>
                </div>

               
            </MainContent>
        </PageContainer>
        <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />
        {accountInfo && <MoveLedgerModal
            show={showMover}
            onClose={() => setShowMover(false)}
            transactionIds={selectedIds}
            accountInfo={accountInfo}
            entries={selectedIds}
        />}
        </>
    );
};

export default AccountStatementPage;
